import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Send, Scale, Info, Sparkles, Plus, MessageSquare, Loader2, MapPin, FileDown,
} from "lucide-react";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { PRESET_PROMPTS, STATES } from "@/data/legalData";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Assistant() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [caseState, setCaseState] = useState("");
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/sessions");
      setSessions(data);
    } catch {}
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const openSession = async (sid) => {
    setSessionId(sid);
    try {
      const { data } = await api.get(`/chat/history/${sid}`);
      setMessages(data.map((m) => ({ role: m.role, content: m.content })));
    } catch {}
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
  };

  const exportBrief = () => {
    if (!user?.is_pro) {
      toast.error("Exporting a Prep Brief is a Case Pass feature.");
      navigate("/upgrade");
      return;
    }
    const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const date = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    const rows = messages
      .filter((m) => m.content?.trim())
      .map((m) => `
        <div class="block ${m.role}">
          <div class="who">${m.role === "user" ? "You asked" : "SentinelGuide Companion"}</div>
          <div class="body">${esc(m.content).replace(/\n/g, "<br/>")}</div>
        </div>`)
      .join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Defense Prep Brief — SentinelGuide</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: Georgia, 'Times New Roman', serif; color: #161E1D; max-width: 720px; margin: 40px auto; padding: 0 24px; line-height: 1.55; }
        .hdr { border-bottom: 3px solid #1B3B32; padding-bottom: 16px; margin-bottom: 24px; }
        .hdr h1 { font-size: 28px; margin: 0 0 4px; color: #112922; }
        .meta { font-size: 12px; color: #5E716D; letter-spacing: .06em; text-transform: uppercase; }
        .block { margin: 0 0 18px; padding: 14px 16px; border-radius: 10px; }
        .block.user { background: #F0F5F3; border: 1px solid #E3EDE9; }
        .block.assistant { background: #FBF9F5; border: 1px solid #EDE4D4; }
        .who { font-size: 11px; text-transform: uppercase; letter-spacing: .1em; color: #2F6557; margin-bottom: 6px; font-family: Arial, sans-serif; }
        .body { font-size: 14.5px; }
        .disc { margin-top: 28px; padding-top: 14px; border-top: 1px solid #DDD0B9; font-size: 11px; color: #899A97; }
        @media print { body { margin: 0; } }
      </style></head>
      <body>
        <div class="hdr">
          <h1>Defense Prep Brief</h1>
          <div class="meta">Prepared with SentinelGuide · ${date}${caseState ? " · " + esc(caseState) : ""}</div>
        </div>
        ${rows || "<p>No conversation yet.</p>"}
        <div class="disc">This document summarizes an educational conversation with the SentinelGuide AI companion. It is general legal information only and is NOT legal advice or a substitute for a licensed attorney. Bring it to your lawyer or public defender to discuss your specific situation.</div>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to export your brief."); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId, state: caseState }),
      });
      if (!res.ok) {
        let detail = "Something went wrong. Please try again.";
        try { const j = await res.json(); detail = j.detail || detail; } catch {}
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: detail }; return c; });
        if (res.status === 402) { toast.error("Unlock the Case Pass for unlimited AI help."); setTimeout(() => navigate("/upgrade"), 900); }
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.session_id && !sessionId) {
            setSessionId(payload.session_id);
            loadSessions();
          }
          if (payload.content) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.content };
              return copy;
            });
          }
          if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: payload.error };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Something went wrong reaching the assistant. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
      loadSessions();
    }
  };

  return (
    <div className="grain-bg min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Sidebar */}
          <aside className="lg:col-span-3">
            <Button onClick={newChat} data-testid="new-chat-btn" className="w-full rounded-xl bg-forest-800 hover:bg-forest-900 text-parchment-50 gap-2 mb-4">
              <Plus className="h-4 w-4" /> New conversation
            </Button>
            <div className="rounded-2xl border border-parchment-200 bg-white p-3">
              <p className="font-mono text-xs uppercase tracking-wider text-ink-400 px-2 py-1">History</p>
              <div className="space-y-1 max-h-[340px] overflow-y-auto thin-scroll">
                {sessions.length === 0 && <p className="text-sm text-ink-400 px-2 py-2">No conversations yet.</p>}
                {sessions.map((s) => (
                  <button key={s.session_id} onClick={() => openSession(s.session_id)} data-testid={`session-${s.session_id}`}
                    className={`w-full text-left rounded-xl px-3 py-2.5 text-sm flex items-start gap-2 transition-colors ${
                      s.session_id === sessionId ? "bg-forest-100 text-forest-900" : "hover:bg-parchment-100 text-ink-700"}`}>
                    <MessageSquare className="h-4 w-4 mt-0.5 shrink-0 text-forest-600" />
                    <span className="line-clamp-2">{s.title}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4 rounded-2xl border border-parchment-200 bg-white p-4">
              <div className="flex items-center gap-2 text-forest-700 mb-2">
                <MapPin className="h-4 w-4" />
                <span className="font-mono text-xs uppercase tracking-wider">Jurisdiction</span>
              </div>
              <Select value={caseState} onValueChange={setCaseState}>
                <SelectTrigger data-testid="assistant-state-select" className="rounded-xl border-parchment-200 h-10 text-sm"><SelectValue placeholder="Any / general" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {STATES.map((s) => <SelectItem key={s.abbr} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </aside>

          {/* Chat */}
          <section className="lg:col-span-9">
            <div className="rounded-3xl border border-parchment-200 bg-white overflow-hidden flex flex-col h-[calc(100vh-140px)] min-h-[560px]">
              <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-parchment-200 bg-parchment-50">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-10 w-10 rounded-xl bg-forest-800 text-parchment-50"><Scale className="h-5 w-5" /></span>
                  <div>
                    <h1 className="font-serif text-xl font-semibold text-forest-900 leading-none">Legal Case Companion</h1>
                    <p className="text-xs text-ink-500 mt-1">Calm, plain-English answers · {user?.name}</p>
                  </div>
                </div>
                {messages.length > 0 && (
                  <Button onClick={exportBrief} data-testid="export-brief-btn" variant="outline"
                    className="rounded-full border-parchment-200 bg-white text-forest-800 gap-2 shrink-0">
                    <FileDown className="h-4 w-4" /> <span className="hidden sm:inline">Export brief</span>
                  </Button>
                )}
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto thin-scroll px-4 sm:px-6 py-6">
                {messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center max-w-lg mx-auto">
                    <span className="grid place-items-center h-16 w-16 rounded-2xl bg-forest-100 text-forest-800 mb-5"><Sparkles className="h-8 w-8" /></span>
                    <h2 className="font-serif text-3xl font-semibold text-forest-900">How can I help you understand your case?</h2>
                    <p className="mt-3 text-ink-500">Ask anything about charges, court procedures, your rights, or your options. Try one of these:</p>
                    <div className="mt-6 grid sm:grid-cols-2 gap-3 w-full">
                      {PRESET_PROMPTS.map((p, i) => (
                        <button key={i} data-testid={`preset-prompt-${i}`} onClick={() => send(p)}
                          className="text-left rounded-xl border border-parchment-200 bg-parchment-50 hover:border-forest-600/40 hover:bg-white p-3.5 text-sm text-ink-700 transition-all">
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6 max-w-3xl mx-auto">
                    {messages.map((m, i) => (
                      <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                        className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        {m.role === "assistant" && (
                          <span className="grid place-items-center h-8 w-8 rounded-lg bg-forest-800 text-parchment-50 shrink-0"><Scale className="h-4 w-4" /></span>
                        )}
                        <div data-testid={`chat-msg-${m.role}-${i}`} className={`rounded-2xl px-4 py-3 max-w-[85%] leading-relaxed whitespace-pre-wrap ${
                          m.role === "user" ? "bg-forest-800 text-parchment-50" : "bg-parchment-100 text-ink-900 border border-parchment-200"}`}>
                          {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin text-forest-600" /> : "")}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-parchment-200 p-4 bg-parchment-50">
                <div className="flex items-start gap-2 mb-2 text-xs text-ink-400">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>Educational &amp; informational only — not formal legal advice. Consult a licensed attorney.</span>
                </div>
                <div className="flex gap-2 items-end">
                  <Textarea
                    data-testid="assistant-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Ask about your case…"
                    className="resize-none rounded-xl border-parchment-200 bg-white min-h-[52px] max-h-32"
                    rows={1}
                  />
                  <Button onClick={() => send()} data-testid="assistant-send" disabled={streaming || !input.trim()}
                    className="rounded-xl h-[52px] w-[52px] p-0 bg-forest-800 hover:bg-forest-900 text-parchment-50 shrink-0">
                    {streaming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
